const { config, fetchHtml } = window.nt;

/**
 * <predictive-search> — the search dialog opened from the header, with suggestions while typing.
 *
 * Without JavaScript the header's search icon is a normal link to the search page. With it, the icon opens this
 * dialog; typing (after a short pause) asks Shopify's predictive search for products, collections, articles,
 * pages and query suggestions, rendered on the server by sections/predictive-search.liquid.
 * Pressing Enter still submits the form to the full search page.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('input[name="q"]');
    this.results = this.querySelector('[data-search-results]');
    this.timer = null;
    this.controller = null;

    document.addEventListener('click', this.onDocumentClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.input.addEventListener('input', this.onInput);
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick = (event) => {
    if (!event.target.closest('[data-search-trigger]')) return;
    event.preventDefault();
    this.dialog.showModal();
    this.input.focus();
  };

  onDialogClick = (event) => {
    if (event.target === this.dialog || event.target.closest('[data-search-close]')) this.dialog.close();
  };

  onClose = () => {
    this.controller?.abort();
    this.results.replaceChildren();
    this.input.value = '';
  };

  onInput = () => {
    clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.controller?.abort();
      this.results.replaceChildren();
      return;
    }
    this.timer = setTimeout(() => this.search(query), 250);
  };

  async search(query) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,article,page,query',
      'resources[limit]': '4',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    try {
      const doc = await fetchHtml(`${config.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
      const found = doc.querySelector('[data-predictive-results]');
      if (found) this.results.replaceChildren(found);
    } catch (error) {
      if (error.name !== 'AbortError') this.results.replaceChildren();
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
